// Copyright 2025, University of Maryland, All Rights Reserved
import { useQuery } from '@tanstack/react-query';
import { useWorkspaceApi, type WorkspaceApi } from '../services/workspace-api';
import type { WorkspaceInfo } from '../types/workspace';

/**
 * Standardized query key factory for workspaces data.
 * Using consistent keys ensures all components share the same cached data.
 */
export const workspacesQueryKey = (orgId: string) => ['workspaces', orgId];

interface UseOrgWorkspacesOptions {
  /** Optional API instance for testing */
  workspaceApi?: WorkspaceApi;
}

/**
 * React Query hook for fetching workspaces for a specific organization.
 * Uses shared query cache to prevent duplicate API calls.
 */
export function useOrgWorkspaces(orgId: string | null | undefined, options: UseOrgWorkspacesOptions = {}) {
  const defaultApi = useWorkspaceApi();
  const workspaceApi = options.workspaceApi ?? defaultApi;

  return useQuery<WorkspaceInfo[]>({
    queryKey: orgId ? workspacesQueryKey(orgId) : ['workspaces', 'none'],
    queryFn: () => {
      if (!orgId) {
        throw new Error('Organization ID is required');
      }
      return workspaceApi.fetchOrgWorkspaces(orgId);
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
